import { Injectable, inject } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import moment from 'moment-timezone';

import { FilteringService } from './filtering.service';

/**
 * SD-7878 — type-aware value comparison for table sorting.
 *
 * Shared, host-agnostic comparator used by {@link NestedSortService} (and available to any table
 * surface). It orders values by their REAL type so financial report columns sort correctly:
 *
 *  - moment / Date          -> chronological
 *  - numbers & booleans     -> numeric (0 is a real value, negatives order correctly)
 *  - numeric-looking strings-> numeric, incl. currency ("$1,234.56"), percent ("12.5%"),
 *                              and accounting-negative parens ("(1,234.56)" = -1234.56)
 *  - everything else        -> house alphanumeric ordering (reuses
 *                              FilteringService.normalizeMixedDataValueForSorting, so plain-text
 *                              columns match the existing app-table sort, incl. natural
 *                              number-in-string ordering like "Item 2" < "Item 10")
 *
 * Blank values (null / undefined / empty-or-whitespace string) always sort LAST, in both
 * directions. Note `0`, `false`, and negative numbers are NOT blank.
 *
 * Deliberate limitation: an ambiguous DATE STRING (e.g. "07/09/2026") is NOT parsed as a date —
 * it falls to string ordering, which is not chronological. Columns that must sort by date should
 * feed the comparator a moment/Date (via the consumer's accessor) rather than a formatted string.
 */
@Injectable({ providedIn: 'root' })
export class NestedSortComparatorService {
    private readonly _filtering = inject(FilteringService);

    /** Compare two values for the given sort direction. Blanks sort last regardless of direction. */
    compare(a: unknown, b: unknown, direction: SortDirection): number {
        const aBlank = this.isBlank(a);
        const bBlank = this.isBlank(b);
        if (aBlank && bBlank) {
            return 0;
        }
        if (aBlank) {
            return 1;
        }
        if (bBlank) {
            return -1;
        }

        const comparison = this.compareNonBlank(a, b);

        return direction === 'desc' ? -comparison : comparison;
    }

    /** Blank = absent or empty/whitespace text. `0`, `false`, and negatives are NOT blank. */
    private isBlank(value: unknown): boolean {
        return value === null
            || value === undefined
            || (typeof value === 'string' && value.trim() === '');
    }

    private compareNonBlank(a: unknown, b: unknown): number {
        const aTime = this.toTime(a);
        const bTime = this.toTime(b);
        if (aTime !== null && bTime !== null) {
            return Math.sign(aTime - bTime);
        }

        const aNum = this.toNumber(a);
        const bNum = this.toNumber(b);
        if (aNum !== null && bNum !== null) {
            return Math.sign(aNum - bNum);
        }

        const aText = this._filtering.normalizeMixedDataValueForSorting(a);
        const bText = this._filtering.normalizeMixedDataValueForSorting(b);

        return aText < bText ? -1 : aText > bText ? 1 : 0;
    }

    /** Epoch millis for a moment/Date, else null (strings are intentionally NOT date-parsed). */
    private toTime(value: unknown): number | null {
        if (moment.isMoment(value)) {
            return value.valueOf();
        }
        if (value instanceof Date) {
            return value.getTime();
        }

        return null;
    }

    /**
     * Coerce a value to a number for numeric ordering, or null when it is not numeric.
     * Handles booleans, plain numbers, and numeric strings including currency symbols, thousands
     * separators, a trailing percent, and accounting-style negative parens.
     */
    private toNumber(value: unknown): number | null {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }
        if (typeof value === 'boolean') {
            return value ? 1 : 0;
        }
        if (typeof value !== 'string') {
            return null;
        }

        let text = value.trim();
        let negative = false;

        if (/^\(.*\)$/.test(text)) {
            negative = true;
            text = text.slice(1, -1);
        }

        text = text.replace(/[$,\s]/g, '').replace(/%$/, '');

        if (text.startsWith('-')) {
            negative = true;
            text = text.slice(1);
        }

        if (!/^\d*\.?\d+$/.test(text)) {
            return null;
        }

        const parsed = Number(text);
        if (!Number.isFinite(parsed)) {
            return null;
        }

        return negative ? -parsed : parsed;
    }
}
